import { Box, Button, Grid, IconButton, List, ListItem, ListItemText, Stack, styled } from "@mui/material";
import React from "react";
import {
  CheckboxComponent,
  DateField,
  SelectFieldComponent,
} from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import GenericForm from "../external/ensolvers-core-frontend-mui/components/Common/GenericForm";
import { Documentation } from "../models/Documentation";
import Timeoff from "../models/Timeoff";
import CustomDateFnsUtils from "../utils/CustomDateFnsUtils";
import SearchIcon from "@mui/icons-material/Search";
import { TimeOffType } from "../types/TimeOffType";
import TimeOffDocumentationList from "./TimeOffDocumentationList";
import { CircularProgress } from "@material-ui/core";
import { useStyles } from "./Main/formStyles";
import { ColorUtils } from "../utils/ColorUtils";

interface Props {
  editTimeoff: Timeoff | undefined;
  selectedFiles: Documentation[];
  onClose: () => void;
  onSave: (instance: Timeoff) => void;
  handleUploadDocument: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteDocument: (files: Documentation[]) => void;
  canEditDocumentation: (instance: Timeoff) => boolean;
  isLoading: boolean;
}

export default function TimeoffEditDialog(props: Props) {
  const customStyles = useStyles();
  
  const getDocumentationList = () => {
    return (
      <List dense>
        {props.selectedFiles.map((item: Documentation, index: number) => (
          <ListItem
            key={index}
            secondaryAction={
              <>
                {item.url && (
                  <IconButton
                    edge="end"
                    aria-label="view"
                    onClick={() => {
                      window.open(item.url, "_blank");
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                )}
              </>
            }
          >
            <ListItemText primary={item?.name?.length > 60 ? item?.name?.substring(0, 60).concat("...") : item.name || item.url} />
          </ListItem>
        ))}
      </List>
    );
  };

  if (!props.editTimeoff) {
    return null;
  }
  return (
    <GenericForm<Timeoff>
      headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
      buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
      cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
      dialogStyles={customStyles.root}      
      dialog={true}
      open={!!props.editTimeoff}
      instance={props.editTimeoff}
      title={`${props.editTimeoff.requester.firstName} ${props.editTimeoff.requester.lastName} - Time Off`}
      onSave={(instance) => {
        props.onSave(instance);
        props.onClose();
      }}
      onCancel={() => props.onClose()}
      dialogCustomSubmitMessage={"Save"}
      renderContent={(register, errors, control) => (
        <>
          <SelectFieldComponent
            control={control}
            register={register}
            errors={errors}
            propertyName={"type"}
            label={"Time Off Type"}
            required={true}
            options={[
              {
                label: "Planned Vacation",
                value: TimeOffType.VACATION,
              },
              {
                label: "Other",
                value: TimeOffType.OTHER,
              },
            ]}
          />

          <DateField
            control={control}
            register={register}
            errors={errors}
            propertyName={"shortStartDate"}
            label="Start Date"
            required={true}
            utils={CustomDateFnsUtils}
          />

          <DateField
            control={control}
            register={register}
            errors={errors}
            propertyName={"shortEndDate"}
            label="End Date"
            required={true}
            utils={CustomDateFnsUtils}
          />
          <CheckboxComponent
            control={control}
            register={register}
            errors={errors}
            propertyName={"halfDay"}
            label="Half day"
            required={false}
          />
          <Grid>
            {!props.canEditDocumentation(props.editTimeoff!) ? (
              getDocumentationList()
            ) : (
              <>
                <TimeOffDocumentationList
                  viewTimeoff={undefined}
                  selectedFiles={props.selectedFiles}
                  onDelete={(files: Documentation[]) => props.handleDeleteDocument(files)}
                />
                <Stack direction="row">
                  <label htmlFor="contained-button-file">
                    <Input
                      id="contained-button-file"
                      type="file"
                      accept=".pdf"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.handleUploadDocument(e)}
                    />
                    {props.isLoading ? (
                      <CircularProgress />
                    ) : (
                      <Button variant="contained" component="span" sx={{ margin: ".7em" }}>
                        Add document
                      </Button>
                    )}
                  </label>
                </Stack>
              </>
            )}
          </Grid>
        </>
      )}
    />
  );
}

const Input = styled("input")({
  display: "none",
});
